import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarDays, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import * as LucideIcons from "lucide-react";

interface Template {
  template_id: string;
  name: string;
  description: string;
  icon?: string;
  background_class?: string;
  questions?: string[];
}

interface TemplateSelectionProps {
  onSelect: (templateId: string) => void;
  onBack: () => void;
  formData: {
    name: string;
  };
  setFormData?: (data: { name: string }) => void;
  isSubmitting?: boolean;
  onContinue: () => void;
  defaultSelectedTemplate?: string;
}

export function TemplateSelection({ onSelect, onBack, formData, setFormData, isSubmitting = false, onContinue, defaultSelectedTemplate }: TemplateSelectionProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(defaultSelectedTemplate || null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [questionsMap, setQuestionsMap] = useState<Record<string, string[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTemplatesAndQuestions = async () => {
      try {
        const { data: templatesData, error: templatesError } = await supabase
          .from('templates')
          .select(`
            template_id,
            name,
            description,
            icon,
            background_class
          `)
          .order('name');
        if (templatesError) throw templatesError;
        setTemplates(templatesData);
        // Fetch questions for all templates
        const templateIds = templatesData.map((t: Template) => t.template_id);
        if (templateIds.length > 0) {
          const { data: tqData, error: tqError } = await supabase
            .from('templatequestions')
            .select('template_id, question_id, display_order, questions:question_id(header)')
            .in('template_id', templateIds)
            .order('display_order');
          if (tqError) throw tqError;
          // Type for tqData row
          type TQRow = { template_id: string; questions?: { header?: string } };
          const map: Record<string, string[]> = {};
          for (const row of tqData as TQRow[]) {
            if (!map[row.template_id]) map[row.template_id] = [];
            map[row.template_id].push(row.questions && typeof row.questions.header === 'string' ? row.questions.header : '');
          }
          setQuestionsMap(map);
        }
      } catch (err: any) {
        console.error('Error fetching templates/questions:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTemplatesAndQuestions();
  }, []);

  useEffect(() => {
    if (defaultSelectedTemplate && defaultSelectedTemplate !== selectedTemplate) {
      setSelectedTemplate(defaultSelectedTemplate);
    }
  }, [defaultSelectedTemplate]);

  const handleSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    onSelect(templateId);
  };

  const handleContinue = () => {
    if (selectedTemplate) {
      onContinue();
    }
  };

  if (isLoading) {
    return (
      <Card className="w-full max-w-3xl mx-auto">
        <CardContent className="py-10">
          <div className="flex justify-center items-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full max-w-3xl mx-auto">
        <CardContent className="py-10">
          <div className="text-center text-destructive">
            <p>Failed to load templates</p>
            <p className="text-sm">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl">Select a Template</CardTitle>
        <CardDescription>
          Choose a template to get started.
          {selectedTemplate && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Template selected
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {templates.map((template) => {
            // LucideIcons does not have an index signature, so we use 'as any' for dynamic access
            let IconComponent: React.FC<{ className?: string }> | undefined = undefined;
            const iconKey = template.icon
              ? template.icon.charAt(0).toUpperCase() + template.icon.slice(1).replace(/-([a-z])/g, (g) => g[1].toUpperCase())
              : undefined;
            if (iconKey && typeof (LucideIcons as any)[iconKey] === 'function') {
              IconComponent = (LucideIcons as any)[iconKey] as React.FC<{ className?: string }>;
            }
            const questions = questionsMap[template.template_id] || [];
            return (
              <div
                key={template.template_id}
                className={cn(
                  "flex flex-col border rounded-xl p-4 cursor-pointer transition-all relative",
                  selectedTemplate === template.template_id
                    ? "border-primary ring-2 ring-primary/10 bg-primary/5"
                    : "border-border hover:border-primary/50"
                )}
                onClick={() => handleSelect(template.template_id)}
              >
                {selectedTemplate === template.template_id && (
                  <div className="absolute top-2 right-2">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary text-white">
                      ✓
                    </span>
                  </div>
                )}
                <div className={cn("rounded-lg p-3 w-fit mb-3", template.background_class)}>
                  {IconComponent ? <IconComponent className="h-6 w-6" /> : <CalendarDays className="h-6 w-6" />}
                </div>
                <h3 className="font-medium mb-1 text-lg">{template.name}</h3>
                <p className="text-sm text-muted-foreground mb-2">{template.description}</p>
                {questions.length > 0 && (
                  <div className="mt-2 border-t pt-2">
                    <p className="text-xs text-muted-foreground mb-1">Questions:</p>
                    <ul className="text-xs space-y-1 list-disc pl-4">
                      {questions.map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="pt-6">
          <label htmlFor="checkin-name" className="block text-2xl font-semibold mb-2">Name your Check-In</label>
          <input
            id="checkin-name"
            type="text"
            className="flex h-11 w-full rounded-lg border border-input bg-background px-4 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-base file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 shadow-sm"
            placeholder="e.g. Team Daily Check-in"
            value={formData.name}
            onChange={e => setFormData && setFormData({ name: e.target.value })}
            autoFocus
          />
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={isSubmitting}
        >
          Back
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!selectedTemplate || !formData.name || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            "Continue"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}